/**
 * Collection Filters
 *
 * @module collection-filters
 * @version 1.0.0
 * @extends HTMLElement
 */

customElements.component = "collection-filters";

if (!customElements.get(customElements.component)) {
  class CollectionFilters extends HTMLElement {
    /**
     * Collection filters web component.
     *
     * @constructor
     */
    constructor() {
      super();

      // This variable is used to store data as cache
      this.filterData = [];

      this.initEvents();

      this.renderCounts();
    }

    /**
     * Init component events.
     *
     * @returns {undefined}
     */
    initEvents() {
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

      this.debouncedOnSubmit = window.theme.debounce.apply((event) => {
        this.onSubmitHandler(event);
      }, 500);

      this.querySelector("form").addEventListener(
        "input",
        this.debouncedOnSubmit.bind(this)
      );

      window.addEventListener("popstate", this.onHistoryChange.bind(this));
    }

    /**
     * On submit handler event.
     *
     * @param {object} event Event.
     * @returns {undefined}
     */
    onSubmitHandler(event) {
      event.preventDefault();

      const formData = new FormData(event.target.closest("form"));

      const searchParams = new URLSearchParams(formData).toString();

      this.renderPage(searchParams, event);
    }

    /**
     * On active filter click event.
     *
     * @param {object} event Click event.
     * @returns {undefined}
     */
    onActiveFilterClick(event) {
      event.preventDefault();

      this.renderPage(
        new URL(event.currentTarget.href).searchParams.toString()
      );
    }

    /**
     * Render page content.
     *
     * @param {string} searchParams URL params.
     * @param {object} event Click event.
     * @param {boolean} updateURLHash update URL hash.
     * @returns {undefined}
     */
    // renderPage(searchParams, event, updateURLHash = true) {
    //   document.getElementById("CollectionProductGrid").classList.add("loading");

    //   const url = `${window.location.pathname}?${searchParams}`;

    //   const filterDataUrl = (element) => element.url === url;

    //   this.filterData.some(filterDataUrl)
    //     ? this.renderSectionFromCache(filterDataUrl, event)
    //     : this.renderSectionFromFetch(url, event);

    //   if (updateURLHash) this.updateURLHash(searchParams);
    // }

    renderPage(searchParams, event, updateURLHash = true) {
      document.getElementById("CollectionProductGrid").classList.add("loading");

      // Parse existing URL parameters
      const existingParams = new URLSearchParams(window.location.search);

      // Parse the new filter parameters
      const newParams = new URLSearchParams(searchParams);

      // Merge new filter parameters into existing ones
      for (const [key, value] of newParams) {
        existingParams.set(key, value); // This updates or adds new parameters
      }

      // Construct the new URL with merged parameters
      const mergedParams = existingParams.toString();
      const url = `${window.location.pathname}?${mergedParams}`;

      // Define the function to match the URL with filter data
      const filterDataUrl = (element) => element.url === url;

      // Use cache or fetch new data based on the merged URL
      this.filterData.some(filterDataUrl)
        ? this.renderSectionFromCache(filterDataUrl, event)
        : this.renderSectionFromFetch(url, event);

      // Update URL hash if needed, using the merged parameters
      if (updateURLHash) this.updateURLHash(mergedParams);
    }

    /**
     * Render section from fetch.
     *
     * @param {string} url String url.
     * @param {object} event Event.
     * @returns {undefined}
     */
    renderSectionFromFetch(url, event) {
      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = responseText;

          const html_dom = new DOMParser().parseFromString(html, "text/html");

          this.filterData = [...this.filterData, { html, url }];

          this.renderFilters(html_dom, event);

          this.renderProductGrid(html_dom);

          this.renderProductCount(html_dom);
        });
    }

    /**
     * Render section from catch.
     *
     * @param {function} filterDataUrl A function.
     * @param {object} event Event.
     * @returns {undefined}
     */
    renderSectionFromCache(filterDataUrl, event) {
      const html = this.filterData.find(filterDataUrl).html;

      const html_dom = new DOMParser().parseFromString(html, "text/html");

      this.renderFilters(html_dom, event);

      this.renderProductGrid(html_dom);

      this.renderProductCount(html_dom);
    }

    /**
     * Render product grid.
     *
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderProductGrid(html_dom) {
      let productGrid = document.getElementById("CollectionProductGrid");

      if (productGrid) {
        let newProductGrid = html_dom.getElementById("CollectionProductGrid");

        if (newProductGrid) {
          productGrid.innerHTML = newProductGrid.innerHTML;

          document
            .getElementById("CollectionProductGrid")
            .classList.remove("loading");
        }
      }
    }

    /**
     * Render product count.
     *
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderProductCount(html_dom) {
      let productQty = document.querySelectorAll(
        "[data-collection-filter-product-qty]"
      );

      let newproductQty = html_dom.querySelector(
        "[data-collection-product-qty]"
      );

      if (newproductQty) {
        productQty.forEach((elem) => {
          elem.innerHTML = newproductQty.innerHTML;
        });
      }
    }

    /**
     * Render filters.
     *
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderFilters(html_dom, event) {
      const facetDetailsElements = html_dom.querySelectorAll(
        "#CollectionFiltersForm .js-filter"
      );

      const matchesIndex = (element) => {
        let result =
          element.dataset.index ===
          event?.target.closest(".js-filter")?.dataset.index;

        return result;
      };

      const facetsToRender = Array.from(facetDetailsElements).filter(
        (element) => !matchesIndex(element)
      );

      facetsToRender.forEach((element) => {
        document.querySelector(
          `.js-filter[data-index="${element.dataset.index}"]`
        ).innerHTML = element.innerHTML;
      });

      this.renderActiveFacets(html_dom);

      this.renderAdditionalElements(html_dom);

      this.renderCounts();
    }

    /**
     * Render active facets.
     *
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderActiveFacets(html_dom) {
      const activeFacetElementSelectors = [".active-facets"];

      activeFacetElementSelectors.forEach((selector) => {
        const activeFacetsElement = html_dom.querySelector(selector);

        if (!activeFacetsElement) return;

        document.querySelector(selector).innerHTML =
          activeFacetsElement.innerHTML;
      });
    }

    /**
     * Render additional elements.
     *
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderAdditionalElements(html_dom) {
      const mobileElementSelectors = [
        ".mobile-facets__open",
        ".mobile-facets__count",
        ".sorting",
      ];

      mobileElementSelectors.forEach((selector) => {
        if (!html_dom.querySelector(selector)) return;

        document.querySelector(selector).innerHTML =
          html_dom.querySelector(selector).innerHTML;
      });
    }

    /**
     * On history change.
     *
     * @param {object} event Event.
     * @returns {undefined}
     */
    onHistoryChange(event) {
      const searchParams = event.state?.searchParams || "";

      this.renderPage(searchParams, null, false);
    }

    /**
     * Render counts.
     *
     * @returns {undefined}
     */
    renderCounts() {
      const count = document.getElementById("activeFacetsCount").dataset.count;

      document.querySelectorAll("[data-filter-count]").forEach((element) => {
        element.dataset.filterCount = parseInt(count);
      });
    }

    // /**
    //  * Update URL hash.
    //  *
    //  * @returns {undefined}
    //  */
    // updateURLHash(searchParams) {
    // history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    // }

    /**
     * Update URL hash, ensuring other params aren't lost.
     *
     * @param {string} newSearchParams New URL search parameters.
     * @returns {undefined}
     */
    updateURLHash(newSearchParams) {
      // Parse the current URL's search parameters
      const existingParams = new URLSearchParams(window.location.search);

      // Parse the new search parameters
      const newParams = new URLSearchParams(newSearchParams);

      // Merge new parameters into existing ones
      for (const [key, value] of newParams) {
        existingParams.set(key, value); // This updates or adds new parameters
      }

      // Construct the new URL search string
      const mergedSearchParams = existingParams.toString();

      // Update the browser's history state with the merged search parameters
      history.pushState(
        { searchParams: mergedSearchParams },
        "",
        `${window.location.pathname}?${mergedSearchParams}`
      );
    }
  }

  customElements.define("collection-filters", CollectionFilters);
}
